import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Star, MapPin, Shield, Clock, FileText, Wrench, Flame, Droplets, Bath, Paintbrush, Settings, Siren } from "lucide-react";
import SEO from "../components/SEO";
import { businessInfo, services, testimonials, reassurance, zones } from "../data/content";
import { getHomeJsonLd } from "../data/jsonld";
import { serviceImages } from "../data/images";

const iconMap = { Wrench, Flame, Droplets, Bath, Paintbrush, Settings, Siren, FileText, Clock, MapPin, Shield };

export default function Home() {
  return (
    <>
      <SEO
        title="Pereira Plomberie Chauffage | Artisan Plombier Chauffagiste à Meaux (77)"
        description="Artisan plombier chauffagiste à Meaux (77). Dépannage rapide, installation sanitaire, chauffage, recherche de fuites. Devis gratuit. Appelez le 06 73 87 88 61."
        jsonLd={getHomeJsonLd()}
      />

      {/* HERO */}
      <section className="relative bg-primary text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/70 z-10" />
        <img
          src="https://static.prod-images.emergentagent.com/jobs/fd61461c-3fdd-4e18-a4ac-4a27fc4b9c09/images/0646dd286b0c13b734a92cf18c1af4a949804a1cf4137df41148348c003f725f.png"
          alt="Artisan plombier en intervention chez un particulier à Meaux"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <div className="inline-block bg-secondary text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm mb-6 font-accent">
              Artisan local à Meaux (77)
            </div>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-none mb-6" data-testid="hero-title">
              Plomberie &<br />
              <span className="text-secondary">Chauffage</span>
              <br />à Meaux
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed max-w-lg">
              Dépannage rapide, installation, entretien. Votre artisan de confiance pour tous vos projets et urgences en plomberie et chauffage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={businessInfo.phoneTel}
                className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider py-4 px-8 rounded-sm shadow-lg transition-all active:scale-95 text-base"
                data-testid="hero-cta-phone"
              >
                <Phone size={20} /> {businessInfo.phone}
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wider py-4 px-8 rounded-sm border border-white/20 transition-all text-base"
                data-testid="hero-cta-devis"
              >
                Devis gratuit <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="bg-white border-b border-slate-200" data-testid="reassurance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {reassurance.map((item, i) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`reassurance-${i}`}>
                  <div className="flex-shrink-0 w-10 h-10 bg-secondary/10 flex items-center justify-center rounded-sm">
                    <Icon size={20} className="text-secondary" />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-semibold uppercase tracking-wide">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-24" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-6">
              {businessInfo.name}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Basée à Meaux (77), notre entreprise familiale intervient rapidement pour tous vos besoins en plomberie et chauffage.
              Disponibles, professionnels et réactifs, nous vous accompagnons dans vos projets comme dans vos urgences.
              De la réparation d'une simple fuite à la rénovation complète de votre salle de bain, nous mettons notre savoir-faire à votre service.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-muted py-16 md:py-24" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-accent font-medium tracking-widest text-secondary uppercase mb-2">Ce que nous faisons</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight">
              Nos services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service) => {
              const Icon = iconMap[service.icon];
              const img = serviceImages[service.slug];
              return (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-sm"
                  data-testid={`service-card-${service.slug}`}
                >
                  {img && (
                    <div className="h-48 overflow-hidden">
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  )}
                  <div className="p-6 border-l-4 border-secondary">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 bg-primary flex items-center justify-center rounded-sm group-hover:bg-secondary transition-colors">
                        <Icon size={18} className="text-white" />
                      </div>
                      <h3 className="font-heading text-lg font-semibold uppercase tracking-wide">{service.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{service.shortDesc}</p>
                    <span className="inline-flex items-center gap-1 text-secondary text-sm font-bold uppercase tracking-wider group-hover:gap-2 transition-all">
                      En savoir plus <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider py-3 px-8 rounded-sm text-sm transition-all" data-testid="all-services-link">
              Tous nos services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* URGENCE BANNER */}
      <section className="bg-secondary text-white py-12" data-testid="urgence-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold uppercase tracking-tight mb-3">
            Urgence plomberie ?
          </h2>
          <p className="text-lg mb-6 opacity-90">Fuite d'eau, canalisation bouchée, dégât des eaux ? Nous intervenons rapidement.</p>
          <a
            href={businessInfo.phoneTel}
            className="inline-flex items-center gap-2 bg-white text-secondary font-bold uppercase tracking-wider py-4 px-8 rounded-sm shadow-lg hover:bg-slate-50 transition-all active:scale-95"
            data-testid="urgence-cta-phone"
          >
            <Phone size={20} /> Appeler maintenant : {businessInfo.phone}
          </a>
        </div>
      </section>

      {/* ZONES */}
      <section className="py-16 md:py-24" data-testid="zones-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-accent font-medium tracking-widest text-secondary uppercase mb-2">Proximité</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-6">
                Zones d'intervention
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Nous intervenons rapidement dans un rayon de 25 km autour de Meaux. Que ce soit pour un dépannage urgent ou un projet programmé, nous sommes à votre service.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {zones.map((zone) => (
                  <span
                    key={zone.name}
                    className={`inline-block px-3 py-1 text-sm rounded-sm border ${
                      zone.primary ? "bg-secondary text-white border-secondary font-bold" : "bg-white text-primary border-slate-200"
                    }`}
                  >
                    {zone.name} {zone.zip && `(${zone.zip})`}
                  </span>
                ))}
                <span className="inline-block px-3 py-1 text-sm text-slate-500 border border-dashed border-slate-300 rounded-sm">
                  Et bien d'autres...
                </span>
              </div>
              <Link to="/zones-intervention" className="inline-flex items-center gap-2 text-secondary font-bold uppercase tracking-wider text-sm hover:gap-3 transition-all" data-testid="zones-link">
                Voir toutes les zones <ArrowRight size={14} />
              </Link>
            </div>
            <div className="space-y-6">
              <div className="bg-primary rounded-sm overflow-hidden">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/fd61461c-3fdd-4e18-a4ac-4a27fc4b9c09/images/1ed6ba17c802201e981b2403ff41bc26c61bbdaf18dfe5d82920b16447a3c68d.png"
                  alt="Installation sanitaire et chaudière murale par Pereira Plomberie"
                  className="w-full h-48 object-cover opacity-80"
                  loading="lazy"
                />
                <div className="p-8 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin size={32} className="text-secondary" />
                    <div>
                      <p className="font-heading text-xl font-bold uppercase">Basée à Meaux</p>
                      <p className="text-sm text-slate-400">{businessInfo.address.full}</p>
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-4 mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock size={16} className="text-secondary" />
                      <span>Intervention rapide sur Meaux et alentours</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Shield size={16} className="text-secondary" />
                      <span>Artisan local de confiance</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={16} className="text-secondary" />
                      <span>Disponible 7j/7 pour les urgences</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-muted py-16 md:py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-accent font-medium tracking-widest text-secondary uppercase mb-2">Confiance</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight">
              Ils nous font confiance
            </h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <span className="font-bold">{businessInfo.googleReviewRating}/5</span>
              <span className="text-slate-500 text-sm">({businessInfo.googleReviewCount}+ avis Google)</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-50 p-8 rounded-br-3xl rounded-tl-3xl border border-slate-200" data-testid={`testimonial-${i}`}>
                <div className="flex mb-3">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} size={16} className="text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-slate-700 italic leading-relaxed mb-4">"{t.text}"</p>
                <p className="font-heading font-semibold text-sm uppercase tracking-wide">— {t.author}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href={businessInfo.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-secondary font-bold uppercase tracking-wider text-sm hover:gap-3 transition-all"
              data-testid="google-reviews-link"
            >
              Voir tous les avis sur Google <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4">
            Un projet ? Une urgence ?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
            Contactez-nous pour un devis gratuit et sans engagement. Nous répondons rapidement à toutes vos demandes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider py-4 px-8 rounded-sm shadow-md transition-all active:scale-95"
              data-testid="bottom-cta-devis"
            >
              Demander un devis gratuit
            </Link>
            <a
              href={businessInfo.phoneTel}
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-all"
              data-testid="bottom-cta-phone"
            >
              <Phone size={18} /> Appeler maintenant
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
